import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, RefreshCw, FileText } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { invoiceValidationService } from '../../services/invoiceValidationService';

const UploadInvoice = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    vendorName: '',
    amount: '',
    currency: 'USD',
    validationMethod: 'Rule-Based',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUploadFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        invoiceNumber: uploadFormData.invoiceNumber,
        invoiceDate: uploadFormData.invoiceDate,
        vendorName: uploadFormData.vendorName,
        amount: Number(uploadFormData.amount),
        currency: uploadFormData.currency,
        validationMethod: uploadFormData.validationMethod,
      };
      await invoiceValidationService.validateInvoice(payload);
      success('Invoice submitted for validation');

      // Navigate back to the invoices list
      navigate('/finance/invoices');
    } catch (error) {
      showError(error.message || 'Failed to submit invoice');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/finance/invoices');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Upload Invoice"
        description="Submit a new invoice for AI-powered validation"
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleUploadSubmit}>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FileText size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Invoice Details
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Invoice Number */}
                  <div>
                    <label style={labelStyle}>Invoice Number *</label>
                    <input
                      type="text"
                      name="invoiceNumber"
                      value={uploadFormData.invoiceNumber}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Invoice Date */}
                  <div>
                    <label style={labelStyle}>Invoice Date *</label>
                    <input
                      type="date"
                      name="invoiceDate"
                      value={uploadFormData.invoiceDate}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Vendor Name */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Vendor Name *</label>
                    <input
                      type="text"
                      name="vendorName"
                      value={uploadFormData.vendorName}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Amount & Currency */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '10px', gridColumn: 'span 2' }}>
                    <div>
                      <label style={labelStyle}>Amount *</label>
                      <input
                        type="number"
                        name="amount"
                        step="0.01"
                        min="0"
                        value={uploadFormData.amount}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Currency *</label>
                      <input
                        type="text"
                        name="currency"
                        value={uploadFormData.currency}
                        onChange={handleChange}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Validation Method */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Validation Method *</label>
                    <select
                      name="validationMethod"
                      value={uploadFormData.validationMethod}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Rule-Based">Rule-Based</option>
                      <option value="Manual">Manual</option>
                      <option value="AI-OCR">AI-OCR</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Submit Invoice
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default UploadInvoice;
